import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, GripVertical, RefreshCw, Calendar, Euro, AlertTriangle, Clock, FileText } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { isDemoProject, TrainingBadge } from "@/lib/demoProjects";
import MobileStageBar, { useColumnSnap } from "@/components/pipeline/MobileStageBar";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  } catch { return null; }
};

const STAGE_COLOR = {
  lead: "from-zinc-700 to-zinc-800",
  tech_check: "from-blue-600/40 to-blue-700/20",
  offer_created: "from-amber-500/40 to-amber-600/20",
  offer_signed: "from-[#00E575]/60 to-[#00C964]/30",
  order_confirmed: "from-[#00E575]/40 to-[#00C964]/20",
  installation_scheduled: "from-purple-500/40 to-purple-600/20",
  installation_done: "from-[#00E575]/30 to-[#00C964]/15",
  handover: "from-[#00E575]/80 to-[#00C964]/40",
};

// Iter88zw (2026-02-18) — BzA-Post-Signatur-Stages ans Manager-Kanban anhängen.
// Diese Spalten sind read-only (kein Drag&Drop) — Fortschritt entsteht über
// Dokumenten-Upload im Projekt-Detail. Karten kommen aus /api/bza-kanban und
// tragen bereits sla_status (ok/warning/overdue) für die Umrandung.
const BZA_STAGE_COLOR = "from-red-700/40 to-red-900/20";
const BZA_STATUS_LABELS = {
  ok: "Im Zeitplan",
  warning: "Bald fällig",
  overdue: "Überfällig",
};
const fmtEur = (n) => new Intl.NumberFormat("de-DE", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(n || 0));

export default function ManagerKanban() {
  const [stages, setStages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bza, setBza] = useState({ stages: [], columns: {}, counters: { overdue: 0, warning: 0, ok: 0 } });
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const snap = useColumnSnap();

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, p, b] = await Promise.all([
        api.get("/tracker/stages"),
        api.get("/projects"),
        api.get("/bza-kanban").catch(() => ({ data: { stages: [], columns: {}, counters: { overdue: 0, warning: 0, ok: 0 } } })),
      ]);
      setStages(s.data.stages || []);
      setProjects(p.data || []);
      setBza(b.data || { stages: [], columns: {}, counters: { overdue: 0, warning: 0, ok: 0 } });
    } catch (e) {
      toast.error("Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  // Iter88zz9a8 (2026-09-07) — Doppelungs-Fix: Projekte, die im BzA-Board
  // eine Karte haben, dürfen NICHT gleichzeitig in einer Sales-Spalte
  // erscheinen (sonst steht z. B. Pizzimento sowohl in „Orientierungs-
  // angebot" als auch in „KfW-Förderung").
  const bzaProjectIds = useMemo(() => {
    const s = new Set();
    for (const col of Object.values(bza.columns || {})) {
      for (const c of col || []) if (c?.project_id) s.add(c.project_id);
    }
    return s;
  }, [bza]);

  // BzA-Stufen haben eigene Spalten (bza-kanban) → keine doppelte Sales-Spalte.
  const hiddenSales = useMemo(
    () => new Set(["order_confirmed", ...(bza.stages || []).map((s) => s.key)]),
    [bza.stages],
  );

  const byStage = useMemo(() => {
    const out = {};
    for (const s of stages) out[s.key] = [];
    const keys = stages.map((s) => s.key);
    for (const pr of projects) {
      if (bzaProjectIds.has(pr.id)) continue;  // Doppelung vermeiden
      let k = pr.tracker?.current_stage || "lead";
      // Steht ein Projekt auf einer ausgeblendeten Stufe, ohne BzA-Karte → vorherige sichtbare Spalte
      for (let i = keys.indexOf(k); hiddenSales.has(k) && i > 0; i -= 1) k = keys[i - 1];
      if (!out[k]) out[k] = [];
      out[k].push(pr);
    }
    return out;
  }, [projects, stages, bzaProjectIds, hiddenSales]);

  // Iter88zab (2026-02-18) — Kombinierte Kanban-Reihenfolge:
  //   Sales-Kette bis „Auftrag unterschrieben"
  //   → BzA-Stages 1–5 (bza_requested … first_az_sent)
  //   → Sales-Stage „Anzahlung erhalten" (down_payment_received)
  //   → BzA-Stage 6 (project_planning_done)
  //   → Sales-Stages ab „Übergabe Installation" (installer_handover …)
  //
  // „Auftragsbestätigung" (order_confirmed) wird ausgeblendet — die
  // BzA-Stage „BzA an Kunden und Auftrag bestätigt" (bza_sent_to_customer)
  // deckt diese Semantik jetzt ab.
  const boardEntries = useMemo(() => {
    const HIDDEN_SALES = hiddenSales;
    const bzaKeys = (bza.stages || []).map((s) => s.key);
    const bzaMap = Object.fromEntries((bza.stages || []).map((s) => [s.key, s]));
    // Erste 5 BzA-Stages (order_index < project_planning_done)
    const AB = "order_confirmation_sent";
    const bzaEarly = bzaKeys.filter((k) => k !== "project_planning_done" && k !== AB);
    const bzaLate = bzaMap["project_planning_done"] ? ["project_planning_done"] : [];
    const out = [];
    for (const s of stages) {
      if (HIDDEN_SALES.has(s.key)) continue;
      if (s.key === "down_payment_received") {
        // Sales-Stage einfügen, BzA-6 folgt direkt danach
        out.push({ type: "sales", stage: s });
        for (const bk of bzaLate) out.push({ type: "bza", stage: bzaMap[bk] });
        continue;
      }
      out.push({ type: "sales", stage: s });
      // Auftragsbestätigung direkt nach „Angebot unterschrieben"
      if (s.key === "offer_signed" && bzaMap[AB]) out.push({ type: "bza", stage: bzaMap[AB] });
      const bzaAnchor = stages.some((x) => x.key === "handover_to_backoffice") ? "handover_to_backoffice" : "offer_signed";
      if (s.key === bzaAnchor) {
        for (const bk of bzaEarly) out.push({ type: "bza", stage: bzaMap[bk] });
      }
    }
    return out;
  }, [stages, bza.stages, hiddenSales]);

  const onDragStart = (e, projectId, isBza = false) => {
    e.dataTransfer.setData("text/plain", projectId);
    e.dataTransfer.setData("source-type", isBza ? "bza" : "sales");
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };
  const onDragLeave = () => setDragOverStage(null);

  const onDrop = async (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    const projectId = e.dataTransfer.getData("text/plain");
    if (projectId) await moveProject(projectId, stageKey);
  };

  const moveProject = async (projectId, stageKey) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const currentStage = project.tracker?.current_stage || "lead";
    if (currentStage === stageKey) return;

    const currentIdx = stages.findIndex((s) => s.key === currentStage);
    const targetIdx = stages.findIndex((s) => s.key === stageKey);
    const isBackward = targetIdx < currentIdx;

    setMoving(projectId);
    try {
      const url = isBackward
        ? `/projects/${projectId}/tracker/revert`
        : `/projects/${projectId}/tracker/advance`;
      await api.post(url, { stage: stageKey });
      toast.success(`Status: ${stages[targetIdx]?.label}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verschieben fehlgeschlagen");
    } finally {
      setMoving(null);
    }
  };

  // Iter88zan (2026-02-18) — BzA-Karten via Drag&Drop (super_admin/admin)
  // rückwärts auf eine frühere BzA-Stage setzen. Ruft die Backend-Route
  // /projects/{id}/bza/set-current-stage — die alle Stages ab dem Ziel
  // rückwärts wieder auf offen setzt.
  const onDropBza = async (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    const projectId = e.dataTransfer.getData("text/plain");
    const sourceType = e.dataTransfer.getData("source-type");
    if (!projectId || sourceType !== "bza") return;
    setMoving(projectId);
    try {
      await api.post(`/projects/${projectId}/bza/set-current-stage`, { stage_key: stageKey });
      toast.success(`BzA-Stage: ${(bza.stages || []).find((s) => s.key === stageKey)?.label}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "BzA-Verschieben fehlgeschlagen");
    } finally {
      setMoving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-50">
        <AppHeader title="Kanban-Board" back="/dashboard" />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-[#00E575]" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      <AppHeader title="Kanban" back="/dashboard" right={
        <button onClick={load} className="h-10 w-10 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center" data-testid="kanban-refresh">
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      } />

      <div className="px-4 pt-5 pb-2 max-w-[1800px] mx-auto">
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Vertriebs-Pipeline</h1>
        <p className="text-zinc-400 mt-1 text-sm">
          {projects.length} Projekte über {stages.length} Sales-Stufen + {(bza.stages || []).length} BzA-Stufen ·
          <span className="hidden sm:inline"> Karten per Drag-and-Drop zwischen Sales-Spalten verschieben (rückwärts erlaubt für Manager). BzA-Karten schalten sich per Dokumenten-Upload weiter.</span>
          <span className="sm:hidden"> Tippen Sie auf eine Karte für Details.</span>
        </p>
        {(bza.counters?.overdue > 0 || bza.counters?.warning > 0) && (
          <div className="mt-2 flex items-center gap-3 text-[12px]" data-testid="kanban-bza-counters">
            <span className="text-red-400 inline-flex items-center gap-1"><AlertTriangle size={12} /> {bza.counters.overdue} überfällig</span>
            <span className="text-amber-400 inline-flex items-center gap-1"><Clock size={12} /> {bza.counters.warning} bald fällig</span>
          </div>
        )}
      </div>

      {/* Iter88zz9a9 (2026-09-07) — Board hat eigene max-Höhe + vertikales
          Scrolling, damit `sticky top-0` auf den Column-Headers greift.
          Sonst scrollt das Fenster und Sticky wirkt nicht. */}
      <MobileStageBar
        items={boardEntries.map((en) => ({
          key: en.stage.key, label: en.stage.label,
          count: en.type === "sales" ? (byStage[en.stage.key] || []).length : ((bza.columns || {})[en.stage.key] || []).length,
          warn: en.type === "bza" && ((bza.columns || {})[en.stage.key] || []).some((c) => c.sla_status === "overdue"),
        }))}
        active={snap.active} onPick={snap.scrollTo} testid="kanban-stage-bar"
      />
      <div ref={snap.boardRef} onScroll={snap.onScroll} className="overflow-auto pb-8 max-h-[calc(100vh-200px)] snap-x snap-mandatory sm:snap-none overscroll-x-contain" data-testid="kanban-board">
        <div className="flex gap-3 px-4 min-w-max items-start [&>*]:snap-start [&>*]:scroll-ml-4">
          {boardEntries.map((entry, colIdx) => {
            if (entry.type === "sales") {
              const stage = entry.stage;
              const items = byStage[stage.key] || [];
              const isOver = dragOverStage === stage.key;
              return (
                <div
                  key={`sales-${stage.key}`}
                  data-col-index={colIdx}
                  data-testid={`kanban-column-${stage.key}`}
                  onDragOver={(e) => onDragOver(e, stage.key)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, stage.key)}
                  className={`w-[calc(100vw-2rem)] sm:w-[300px] flex-shrink-0 rounded-2xl border-2 transition ${
                    isOver
                      ? "border-[#00E575] bg-[#00E575]/8"
                      : "border-zinc-800 bg-[#0E0E12]"
                  }`}
                >
                  <div className={`sticky top-0 z-20 bg-gradient-to-br ${STAGE_COLOR[stage.key] || "from-zinc-800 to-zinc-900"} rounded-t-xl px-3 py-3 border-b border-zinc-800 shadow-lg`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-[14px] text-white truncate">{stage.label}</div>
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[#09090B]/70 text-zinc-100 text-[12px] font-mono font-bold">
                        {items.length}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 min-h-[120px]">
                    {items.length === 0 ? (
                      <div className="text-[12px] text-zinc-400 text-center py-6 font-medium">Keine Projekte</div>
                    ) : items.map((p) => (
                      <ProjectCard
                        key={p.id}
                        project={p}
                        onDragStart={onDragStart}
                        isMoving={moving === p.id}
                        stages={stages.filter((s) => !hiddenSales.has(s.key))}
                        onMove={moveProject}
                      />
                    ))}
                  </div>
                </div>
              );
            }
            // BzA-Spalte
            const bstage = entry.stage;
            const cards = (bza.columns && bza.columns[bstage.key]) || [];
            const isOver = dragOverStage === bstage.key;
            return (
              <div
                key={`bza-${bstage.key}`}
                data-col-index={colIdx}
                data-testid={`kanban-bza-column-${bstage.key}`}
                onDragOver={(e) => onDragOver(e, bstage.key)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDropBza(e, bstage.key)}
                className={`w-[calc(100vw-2rem)] sm:w-[300px] flex-shrink-0 rounded-2xl border-2 bg-[#120E10] transition ${
                  isOver ? "border-[#00E575] bg-[#00E575]/5" : "border-red-900/40"
                }`}
              >
                <div className={`sticky top-0 z-20 bg-gradient-to-br ${BZA_STAGE_COLOR} rounded-t-xl px-3 py-3 border-b border-red-900/50 shadow-lg`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-[14px] text-white truncate">{bstage.label}</div>
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[#09090B]/70 text-zinc-100 text-[12px] font-mono font-bold">
                      {cards.length}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-300/80 mt-0.5 leading-snug inline-flex items-center gap-1">
                    <Clock size={10} /> SLA {bstage.sla_days || "–"} T
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {cards.length === 0 ? (
                    <div className="text-[12px] text-zinc-400 text-center py-6 font-medium">Keine Projekte</div>
                  ) : cards.map((c) => (
                    <BzaCard key={c.project_id} card={c} onDragStart={onDragStart} />
                  ))}
                </div>
              </div>
            );
          })}
          {/* Iter88zz9z (2026-09-07) — read-only „An Back-Office"-Column
              entfernt. Der Handover ist jetzt eine reguläre Stage
              `handover_to_backoffice` (order 45, direkt nach Angebot
              unterschrieben), Drag & Drop wie überall sonst. */}
        </div>
      </div>
    </div>
  );
}

function BzaCard({ card, onDragStart }) {
  const isOverdue = card.sla_status === "overdue";
  const isWarning = card.sla_status === "warning";
  const borderCls = isOverdue
    ? "border-2 border-red-500 bg-red-500/10"
    : isWarning
      ? "border-2 border-amber-500 bg-amber-500/10"
      : "border border-zinc-800 bg-[#18181B]";
  const daysOver = card.sla_days > 0 ? card.days_in_stage - card.sla_days : 0;
  return (
    <Link
      to={`/projects/${card.project_id}`}
      draggable={typeof onDragStart === "function"}
      onDragStart={onDragStart ? (e) => onDragStart(e, card.project_id, true) : undefined}
      data-testid={`kanban-bza-card-${card.project_id}`}
      className={`block rounded-xl p-3 hover:brightness-110 transition cursor-grab active:cursor-grabbing ${borderCls}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-display font-bold text-[13.5px] text-white truncate flex-1">
          {card.customer_name}
        </div>
        {isOverdue && <AlertTriangle size={13} className="text-red-400 shrink-0" />}
        {isWarning && <Clock size={13} className="text-amber-400 shrink-0" />}
      </div>
      {card.customer_city && (
        <div className="mt-0.5 text-[11.5px] text-zinc-400 inline-flex items-center gap-1 truncate">
          <MapPin size={10} /> {card.customer_city}
        </div>
      )}
      <div className="mt-1.5 flex items-center justify-between text-[11.5px]">
        <span className="text-zinc-300 font-mono">{fmtEur(card.total_gross)}</span>
        {card.docs_count > 0 && (
          <span className="text-blue-300 inline-flex items-center gap-1">
            <FileText size={10} /> {card.docs_count}
          </span>
        )}
      </div>
      <div className={`mt-1.5 text-[11px] font-semibold ${
        isOverdue ? "text-red-300" : isWarning ? "text-amber-300" : "text-zinc-500"
      }`}>
        {card.days_in_stage} Tage · {BZA_STATUS_LABELS[card.sla_status]}
        {isOverdue && daysOver > 0 && <> · +{daysOver} Tage über SLA</>}
      </div>
    </Link>
  );
}

function ProjectCard({ project, onDragStart, isMoving, stages = [], onMove }) {
  const c = project;
  const stale = project.updated_at
    ? Math.floor((Date.now() - new Date(project.updated_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  return (
    <div className="relative">
    <Link
      to={`/projects/${project.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, project.id)}
      data-testid={`kanban-card-${project.id}`}
      className={`block bg-[#18181B] hover:bg-zinc-800/60 border border-zinc-800 hover:border-[#00E575]/40 rounded-xl p-3 pb-12 sm:pb-3 cursor-grab active:cursor-grabbing transition ${
        isMoving ? "opacity-50" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-zinc-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[14px] text-white truncate flex items-center gap-1.5">
            <span className="truncate">{c.customer_name || "Ohne Namen"}</span>
            {isDemoProject(c) && <TrainingBadge />}
            {c.sales_handover?.done_at && (
              <span className="text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 flex-shrink-0" title="An Back-Office übergeben">
                ✓ BO
              </span>
            )}
          </div>
          {c.customer_city && (
            <div className="text-[12px] text-zinc-400 truncate inline-flex items-center gap-1 mt-0.5 font-medium">
              <MapPin size={11} /> {c.customer_zip} {c.customer_city}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 text-[12px] font-mono">
            <span className="text-[#00E575]">{(c.project_number || "").slice(-9)}</span>
            {stale !== null && (
              <span className={`inline-flex items-center gap-0.5 ${stale > 14 ? "text-amber-400" : "text-zinc-500"}`}>
                <Calendar size={10} /> {stale === 0 ? "heute" : `vor ${stale}T`}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
    {onMove && stages.length > 0 && (
      <select
        value={c.tracker?.current_stage || "lead"}
        onChange={(e) => onMove(c.id, e.target.value)}
        disabled={isMoving}
        data-testid={`kanban-card-move-${c.id}`}
        aria-label="Phase ändern"
        className="sm:hidden absolute left-3 right-3 bottom-2 h-9 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-200 text-[16px] px-2"
      >
        {!stages.some((s) => s.key === (c.tracker?.current_stage || "lead")) && (
          <option value={c.tracker?.current_stage}>Phase ändern …</option>
        )}
        {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
    )}
    </div>
  );
}
